import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { fetchBranches, fetchSubjects } from "@/api/branches";
import { createLead, updateLead } from "@/api/leads";
import { extractErrorMessage } from "@/types/common";
import { LEAD_SOURCE_LABELS, LEAD_STAGES_ORDER, LEAD_STAGE_LABELS } from "@/types/enums";
import type { LeadSource, LeadStage } from "@/types/enums";
import type { Lead } from "@/types/lead";
import { TIME_SLOTS } from "@/pages/groups/GroupFormDialog";

interface FormState {
  fullName: string;
  phone: string;
  age: string;
  branchId: string;
  subjectId: string;
  source: LeadSource;
  stage: LeadStage;
  trialDate: string;
  trialTime: string;
  nextContactDate: string;
  lostReason: string;
  comment: string;
  sendTrialInvite: boolean;
}

const EMPTY: FormState = {
  fullName: "",
  phone: "",
  age: "",
  branchId: "",
  subjectId: "",
  source: "OTHER",
  stage: "NEW",
  trialDate: "",
  trialTime: "",
  nextContactDate: "",
  lostReason: "",
  comment: "",
  sendTrialInvite: false,
};

/** Единая форма заявки: создание новой и редактирование существующей (ТЗ §2.9, прототип editLead()). */
export function LeadEditDialog({ lead, open, onClose, onConvert }: { lead: Lead | null; open: boolean; onClose: () => void; onConvert: (lead: Lead) => void }) {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const { data: subjects } = useQuery({ queryKey: ["subjects"], queryFn: fetchSubjects });

  useEffect(() => {
    if (!open) return;
    setServerError(null);
    if (lead) {
      setForm({
        fullName: lead.fullName,
        phone: lead.phone,
        age: lead.age != null ? String(lead.age) : "",
        branchId: lead.branchId,
        subjectId: lead.subjectId,
        source: lead.source,
        stage: lead.stage,
        trialDate: lead.trialDate?.slice(0, 10) ?? "",
        trialTime: lead.trialTime ?? "",
        nextContactDate: lead.nextContactDate?.slice(0, 10) ?? "",
        lostReason: lead.lostReason ?? "",
        comment: lead.comment ?? "",
        sendTrialInvite: false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, lead]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const mutation = useMutation({
    mutationFn: () => {
      if (lead) {
        return updateLead(lead.id, {
          fullName: form.fullName,
          phone: form.phone,
          age: form.age ? Number(form.age) : undefined,
          branchId: form.branchId,
          subjectId: form.subjectId,
          source: form.source,
          stage: form.stage,
          trialDate: form.trialDate || undefined,
          trialTime: form.trialTime || undefined,
          nextContactDate: form.nextContactDate || null,
          lostReason: form.lostReason || undefined,
          comment: form.comment,
          sendTrialInvite: form.sendTrialInvite,
        } as Partial<Lead> & { sendTrialInvite?: boolean });
      }
      return createLead({
        fullName: form.fullName,
        phone: form.phone,
        age: form.age ? Number(form.age) : undefined,
        branchId: form.branchId,
        subjectId: form.subjectId,
        source: form.source,
        nextContactDate: form.nextContactDate || undefined,
        comment: form.comment || undefined,
        sendTrialInvite: form.sendTrialInvite,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  const canConvert = Boolean(lead) && (lead!.stage === "BOOKED" || lead!.stage === "TRIAL");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{lead ? `Заявка · ${lead.fullName}` : "Новая заявка"}</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} sm={6}>
            <TextField label="Имя" fullWidth value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Телефон" fullWidth value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Возраст" type="number" fullWidth value={form.age} onChange={(e) => set("age", e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Предмет" fullWidth value={form.subjectId} onChange={(e) => set("subjectId", e.target.value)}>
              {(subjects ?? []).map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Филиал" fullWidth value={form.branchId} onChange={(e) => set("branchId", e.target.value)}>
              {(branches ?? []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Источник" fullWidth value={form.source} onChange={(e) => set("source", e.target.value as LeadSource)}>
              {Object.entries(LEAD_SOURCE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {lead && (
            <Grid item xs={6}>
              <TextField select label="Этап" fullWidth value={form.stage} onChange={(e) => set("stage", e.target.value as LeadStage)}>
                {LEAD_STAGES_ORDER.map((stage) => (
                  <MenuItem key={stage} value={stage}>
                    {LEAD_STAGE_LABELS[stage]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              label="Дата пробного"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.trialDate}
              onChange={(e) => set("trialDate", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Время пробного" fullWidth value={form.trialTime} onChange={(e) => set("trialTime", e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {TIME_SLOTS.map((slot) => (
                <MenuItem key={slot} value={slot}>
                  {slot}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Следующий контакт"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.nextContactDate}
              onChange={(e) => set("nextContactDate", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Причина отказа (если отказ)"
              fullWidth
              value={form.lostReason}
              onChange={(e) => set("lostReason", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Комментарий" fullWidth multiline minRows={2} value={form.comment} onChange={(e) => set("comment", e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={form.sendTrialInvite} onChange={(e) => set("sendTrialInvite", e.target.checked)} />}
              label="Отправить приглашение на пробный урок"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {canConvert && (
          <Button
            color="secondary"
            onClick={() => {
              onClose();
              onConvert(lead!);
            }}
          >
            Зачислить в группу
          </Button>
        )}
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
